import React, { useState, useEffect } from "react";
import "./employer.css";
import { Breadcrumb, Image, Table, Spin } from "antd";
import Loader from "../../components/loader/loader";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import { useNavigate } from "react-router-dom";
import { GreenNotify } from "../../helper/helper";

const DAYS_STALE_THRESHOLD = 10;

// Builds { [employerId]: { count, lastDate } } from a flat offers list
const buildOfferStatsByEmployer = (offers) => {
  const statsByEmployer = {};

  (offers || []).forEach((offer) => {
    const employerId = offer?.employer?._id || offer?.employer;
    if (!employerId) return;

    const createdAt = offer?.createdAt ? new Date(offer.createdAt) : null;
    const existing = statsByEmployer[employerId];

    if (!existing) {
      statsByEmployer[employerId] = { count: 1, lastDate: createdAt };
    } else {
      existing.count += 1;
      if (createdAt && (!existing.lastDate || createdAt > existing.lastDate)) {
        existing.lastDate = createdAt;
      }
    }
  });

  return statsByEmployer;
};

const formatDate = (date) => {
  if (!date) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const isStale = (date) => {
  if (!date) return false;
  const diffMs = new Date().setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > DAYS_STALE_THRESHOLD;
};

const OfferStatsCell = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="offer-stats-cell">
        <Spin size="small" />
      </div>
    );
  }

  const count = stats?.count || 0;
  const lastDate = stats?.lastDate || null;

  return (
    <div className="offer-stats-cell">
      <div>{count} offers</div>
      <div
        className="offer-stats-date"
        style={{ color: isStale(lastDate) ? "#ff4d4f" : "#9ca3af" }}
      >
        {formatDate(lastDate)}
      </div>
    </div>
  );
};

const Employer = () => {
  const [isloading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [engOfferStats, setEngOfferStats] = useState({});
  const [araOfferStats, setAraOfferStats] = useState({});
  const [engStatsLoading, setEngStatsLoading] = useState(true);
  const [araStatsLoading, setAraStatsLoading] = useState(true);
  const [getServiceapi, setGetServiceApi] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });

  const getAllEmployers = () => {
    let getRes = (res) => {
      setEmployers(res?.data?.data);
    };

    callApi(
      "GET",
      routes.getAllEmployers,
      null,
      setIsLoading,
      getRes,
      () => {}
    );
  };

  const getAllEngOffers = () => {
    let getRes = (res) => {
      setEngOfferStats(buildOfferStatsByEmployer(res?.data?.data));
    };

    callApi(
      "GET",
      routes.getAllEngOffers,
      null,
      setEngStatsLoading,
      getRes,
      () => {}
    );
  };

  const getAllArabicOffers = () => {
    let getRes = (res) => {
      setAraOfferStats(buildOfferStatsByEmployer(res?.data?.data));
    };

    callApi(
      "GET",
      routes.getAllArabicOffers,
      null,
      setAraStatsLoading,
      getRes,
      () => {}
    );
  };

  const deleteEmployer = (id) => {
    setGetServiceApi(false);
    let getRes = (res) => {
      GreenNotify("Employer is deleted successfully");
      setGetServiceApi(true);
    };

    callApi(
      "DELETE",
      `${routes.deleteEmployer}/${id}`,
      null,
      setIsLoading,
      getRes,
      () => {}
    );
  };

  useEffect(() => {
    getAllEmployers();
    getAllEngOffers();
    getAllArabicOffers();
  }, [getServiceapi]);

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      className: "role-name-column-header",
      align: "center",
      width: 220,
    },
    {
      title: "Employer Code",
      dataIndex: "employerCode",
      className: "role-name-column-header",
      align: "center",
      width: 220,
    },
    {
      title: "Image",
      dataIndex: "image",
      className: "role-name-column-header",
      align: "center",
    },
    {
      title: "Arabic",
      dataIndex: "araStats",
      className: "role-name-column-header",
      align: "center",
    },
    {
      title: "English",
      dataIndex: "engStats",
      className: "role-name-column-header",
      align: "center",
    },

    {
      title: "Actions",
      dataIndex: "actions",
      align: "center",
      className: "action-column-header",
      width: 120,
    },
  ];

  const data = employers?.map((item, index) => {
    return {
      key: index,
      name: item?.name,
      employerCode: item?.employerCode || item?.code || item?.shortCode || "-",
      image: (
        <div className="product-list-image">
          <Image width={50} src={item?.image} alt="image" />
        </div>
      ),
      araStats: (
        <OfferStatsCell
          stats={araOfferStats[item?._id]}
          loading={araStatsLoading}
        />
      ),
      engStats: (
        <OfferStatsCell
          stats={engOfferStats[item?._id]}
          loading={engStatsLoading}
        />
      ),
      actions: (
        <div className="icon-actions-cell">
          <div
            onClick={() => navigate("/editEmployer", { state: { item: item } })}
            className="icon-only-btn icon-only-edit"
            title="Edit"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div
            onClick={() => deleteEmployer(item?._id)}
            className="icon-only-btn icon-only-delete"
            title="Delete"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      ),
    };
  });

  const getRowClassName = (record, index) => {
    if (index % 2 === 0) {
      return "server-role-even-row";
    }
    return "server-role-odd-row";
  };

  return (
    <div className="modern-page-container">
      <Loader loading={isloading} />
      
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <Breadcrumb
              separator="›"
              className="modern-breadcrumb"
              items={[
                { title: "Dashboard" },
                { title: <span className="breadcrumb-active">Employer</span> },
              ]}
            />
            <h1 className="page-title">Employer Management</h1>
            <p className="page-subtitle">Manage and view all employers</p>
          </div>
          <button
            onClick={() => navigate("/addEmployer")}
            className="modern-add-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Add New Employer
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Employers</p>
            <h3 className="stat-value">{employers?.length || 0}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Active</p>
            <h3 className="stat-value">{employers?.length || 0}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 1.17157 16.1716C0.421427 16.9217 0 17.9391 0 19V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Registered</p>
            <h3 className="stat-value">{employers?.length || 0}</h3>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">All Employers</h2>
        </div>

        <div className="table-container">
          <Table
            rowClassName={getRowClassName}
            columns={columns}
            dataSource={data}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} employers`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
          />
        </div>
      </div>
    </div>
  );
};

export default Employer;
